"use strict";
/**
 *
 * Popper
 *
 * Implementation from:
 * https://github.com/mui-org/material-ui/blob/f55f3a4f808d1756d11d056195978affe4f65d5b/packages/material-ui/src/Popper/Popper.js
 *
 */
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (Object.hasOwnProperty.call(mod, k)) result[k] = mod[k];
    result["default"] = mod;
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const react_1 = __importStar(require("react"));
const Portal_1 = require("../Portal");
const popper_js_1 = __importDefault(require("popper.js"));
const hooks_1 = require("../hooks");
const utils_1 = require("../utils");
const theme_1 = require("../styles/theme");
const zIndex_1 = require("../styles/zIndex");
function flipPlacement(placement) {
    const direction = (typeof window !== 'undefined' && document.body.getAttribute('dir')) || 'ltr';
    if (direction !== 'rtl') {
        return placement;
    }
    switch (placement) {
        case 'bottom-end':
            return 'bottom-start';
        case 'bottom-start':
            return 'bottom-end';
        case 'top-end':
            return 'top-start';
        case 'top-start':
            return 'top-end';
        default:
            return placement;
    }
}
function getAnchorEl(anchorEl) {
    return typeof anchorEl === 'function' ? anchorEl() : anchorEl;
}
/**
 * Poppers rely on the 3rd party library [Popper.js](https://github.com/FezVrasta/popper.js) for positioning.
 */
exports.Popper = react_1.default.forwardRef(function Popper(props, ref) {
    const { anchorEl, children, container, disablePortal = false, keepMounted = false, modifiers, open, placement: placementProps = 'bottom', popperOptions, popperRef: popperRefProp } = props, other = __rest(props, ["anchorEl", "children", "container", "disablePortal", "keepMounted", "modifiers", "open", "placement", "popperOptions", "popperRef"]);
    const tooltipRef = react_1.useRef(null);
    const handleRef = hooks_1.useForkRef(tooltipRef, ref);
    const popperRef = react_1.useRef(null);
    const handlePopperRefRef = react_1.useRef(null);
    const handlePopperRef = hooks_1.useForkRef(popperRef, popperRefProp);
    react_1.useLayoutEffect(() => {
        handlePopperRefRef.current = handlePopperRef;
    }, [handlePopperRef]);
    react_1.useImperativeHandle(popperRefProp, () => popperRef.current, []);
    const [placement, setPlacement] = react_1.useState();
    const handleOpen = react_1.useCallback(() => {
        const handlePopperUpdate = (data) => {
            if (data.placement !== placement) {
                setPlacement(data.placement);
            }
        };
        const popperNode = tooltipRef.current;
        if (!popperNode || !anchorEl || !open) {
            return;
        }
        if (popperRef.current) {
            popperRef.current.destroy();
            handlePopperRefRef.current(null);
        }
        const popper = new popper_js_1.default(getAnchorEl(anchorEl), popperNode, Object.assign(Object.assign({ placement: flipPlacement(placementProps) }, popperOptions), { modifiers: Object.assign(Object.assign(Object.assign({}, (disablePortal
                ? {}
                : {
                    // It's using scrollParent by default, we can use the viewport when using a portal.
                    preventOverflow: {
                        boundariesElement: 'window',
                    },
                })), modifiers), (popperOptions ? popperOptions.modifiers : undefined)), 
            // We could have been using a custom modifier like react-popper is doing.
            // But it seems this is the best public API for this use case.
            onCreate: utils_1.createChainedFunction(handlePopperUpdate, popperOptions ? popperOptions.onCreate : undefined), onUpdate: utils_1.createChainedFunction(handlePopperUpdate, popperOptions ? popperOptions.onUpdate : undefined) }));
        handlePopperRefRef.current(popper);
    }, [anchorEl, disablePortal, modifiers, open, placement, placementProps, popperOptions]);
    const handleClose = () => {
        if (!popperRef.current) {
            return;
        }
        popperRef.current.destroy();
        handlePopperRefRef.current(null);
    };
    react_1.useEffect(() => {
        // Let's update the popper position.
        handleOpen();
    }, [handleOpen]);
    react_1.useEffect(() => {
        return () => {
            handleClose();
        };
    }, []);
    react_1.useEffect(() => {
        if (!open) {
            // Otherwise handleExited will call this.
            handleClose();
        }
    }, [open]);
    const theme = theme_1.useTheme();
    if (!keepMounted && !open) {
        return null;
    }
    return (react_1.default.createElement(Portal_1.Portal, { onRendered: handleOpen, disablePortal: disablePortal, container: container },
        react_1.default.createElement("div", Object.assign({ ref: handleRef, role: "tooltip", style: {
                // Prevents scroll issue, waiting for Popper.js to add this style once initiated.
                position: 'absolute',
                zIndex: theme ? theme.zIndex.tooltip : zIndex_1.defaultZIndex.tooltip,
            } }, other), children)));
});
if (process.env.NODE_ENV !== 'production') {
    exports.Popper.displayName = 'Popper';
}
